import React from 'react';
import cn from '@/utils/cn';
import SPAPageHeader from '../shared/SPAPageHeader';

export interface DevTool {
  description: string;
  href: string;
  routerPath?: string;
  title: string;
}

export interface DevDashboardProps {
  description?: string;
  onNavigate?: (path: string) => void;
  title?: string;
  tools?: DevTool[];
}

/* eslint-disable max-len */
const DEFAULT_TOOLS: DevTool[] = [
  { description: 'Browse and edit project documentation.', href: '/sysadmin/dev-docs', routerPath: '/dev-docs', title: 'DevDocs' },
  { description: 'Server health, versions, and database info.', href: '/status', routerPath: '/system-status', title: 'System Status' },
  { description: 'AI adoption-risk review of lessons and library content.', href: '/sysadmin/dev/content-audit', routerPath: '/content-audit', title: 'Content Audit' },
  { description: 'Hallucination and bias screening results for AI-generated questions.', href: '/sysadmin/dev/ai-safety', routerPath: '/ai-safety', title: 'AI Safety' },
  { description: 'Per-user AI spend and rate limits, and who is using AI.', href: '/sysadmin/dev/ai-policy', routerPath: '/ai-policy', title: 'AI Policy' },
  { description: 'Background job dashboard and queues.', href: '/sidekiq', title: 'Sidekiq' },
  { description: 'Database performance and query analysis.', href: '/pghero', title: 'PgHero' },
];
/* eslint-enable max-len */

const cardClasses = cn(
  'tw-block tw-px-5 tw-py-4 tw-rounded-lg tw-no-underline',
  'tw-bg-spa-bg-secondary tw-border tw-border-solid tw-border-spa-border-secondary',
  'hover:tw-bg-spa-bg-hover hover:tw-border-spa-border-hover',
  'tw-transition-colors tw-duration-150',
);

const ToolCardContent = ({ tool }: { tool: DevTool }) => (
  <>
    <div className="tw-text-sm tw-font-medium tw-text-spa-on-bg">{tool.title}</div>
    <div className="tw-text-xs tw-text-spa-secondary tw-mt-1">{tool.description}</div>
  </>
);

// AIDEV-NOTE: Tool-card launcher themed with the SPA tokens. Dev Tools by default; title,
// description, and tools are overridable so other launchers (Langfuse) share the page frame.
// When onNavigate is provided, tools with routerPath navigate client-side (SPA mode).
const DevDashboard = ({
  description = 'Quick access to developer resources and system tools.',
  onNavigate,
  title = 'Dev Tools',
  tools,
}: DevDashboardProps) => {
  const devTools = tools || DEFAULT_TOOLS;

  return (
    <div className="tw-min-h-screen tw-bg-spa-bg tw-text-spa-on-bg">
      <div className="tw-mx-auto tw-max-w-[1400px] tw-px-6 tw-py-4">
        <SPAPageHeader title={title} />
        <p className="tw-mb-5 tw-mt-0 tw-text-sm tw-text-spa-secondary">{description}</p>
        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-3">
          {devTools.map(tool => (
            onNavigate && tool.routerPath ? (
              <button
                key={tool.title}
                className={cn(cardClasses, 'tw-text-left tw-cursor-pointer')}
                type="button"
                onClick={() => onNavigate(tool.routerPath as string)}
              >
                <ToolCardContent tool={tool} />
              </button>
            ) : (
              <a
                key={tool.title}
                className={cardClasses}
                href={tool.href}
              >
                <ToolCardContent tool={tool} />
              </a>
            )
          ))}
        </div>
      </div>
    </div>
  );
};

export default DevDashboard;
